import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select } from '../ui/select';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const numFilter = (v) => v.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
const pad = (n) => String(n).padStart(2, '0');
const lastDay = (y, m) => new Date(y, m, 0).getDate(); // m is 1-based
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };

const Reimbursement = ({ user, token, api }) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const [rates, setRates] = useState([]);
  const [rateInputs, setRateInputs] = useState({});
  const [newYear, setNewYear] = useState(currentYear);
  const [newRate, setNewRate] = useState('');

  const [periodType, setPeriodType] = useState('month');
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      const data = await api.get('/reimbursement-rates', token);
      setRates(data);
      const map = {};
      data.forEach(r => { map[r.year] = String(parseFloat(r.rate)); });
      setRateInputs(map);
    } catch (err) {
      setError(err.message);
    }
  };

  const saveRate = async (yr, rateStr) => {
    try {
      setError(''); setStatus('');
      await api.put('/reimbursement-rates', { year: yr, rate: rateStr }, token);
      setStatus(`Saved ${yr} rate`);
      setNewRate('');
      fetchRates();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteRate = async (yr) => {
    if (!window.confirm(`Delete the ${yr} rate?`)) return;
    try {
      await api.delete(`/reimbursement-rates/${yr}`, token);
      setStatus(`Deleted ${yr} rate`);
      fetchRates();
    } catch (err) {
      setError(err.message);
    }
  };

  const computeRange = () => {
    if (periodType === 'month') {
      return { start: `${year}-${pad(month)}-01`, end: `${year}-${pad(month)}-${pad(lastDay(year, month))}` };
    }
    if (periodType === 'quarter') {
      const sm = (quarter - 1) * 3 + 1, em = quarter * 3;
      return { start: `${year}-${pad(sm)}-01`, end: `${year}-${pad(em)}-${pad(lastDay(year, em))}` };
    }
    if (periodType === 'ytd') {
      return { start: `${currentYear}-01-01`, end: todayStr() };
    }
    return { start: customStart, end: customEnd };
  };

  const handleCalculate = async () => {
    setError(''); setResult(null);
    const { start, end } = computeRange();
    if (!start || !end) { setError('Select a valid date range.'); return; }
    if (start > end) { setError('Start date must be on or before the end date.'); return; }
    try {
      const r = await api.get(`/reimbursement?start=${start}&end=${end}`, token);
      setResult(r);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {error && <div className="text-sm text-destructive">{error}</div>}
      {status && <div className="text-sm text-emerald-500">{status}</div>}

      {/* Per-year rates */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Reimbursement Rates ($ per mile)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">Each year uses its own rate, so updating a future year never changes prior-year reports.</p>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[420px]">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="pb-2 w-24">Year</th>
                <th className="pb-2">Rate ($/mi)</th>
                <th className="pb-2 w-40"></th>
              </tr>
            </thead>
            <tbody>
              {rates.map(r => (
                <tr key={r.year} className="border-b border-border/50">
                  <td className="py-1 font-mono">{r.year}</td>
                  <td className="py-1">
                    <Input
                      value={rateInputs[r.year] ?? ''}
                      onChange={e => setRateInputs({ ...rateInputs, [r.year]: numFilter(e.target.value) })}
                      className="h-8 w-28"
                    />
                  </td>
                  <td className="py-1 text-right">
                    <Button variant="ghost" size="sm" onClick={() => saveRate(r.year, rateInputs[r.year])}>Save</Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteRate(r.year)}>Delete</Button>
                  </td>
                </tr>
              ))}
              {rates.length === 0 && (
                <tr><td colSpan={3} className="py-2 text-muted-foreground">No rates set yet. Add one below.</td></tr>
              )}
            </tbody>
          </table>
          </div>

          <div className="flex items-end gap-2 pt-2 border-t flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Year</Label>
              <Input type="number" value={newYear} onChange={e => setNewYear(e.target.value)} className="h-8 w-24" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Rate ($/mi)</Label>
              <Input value={newRate} onChange={e => setNewRate(numFilter(e.target.value))} placeholder="0.70" className="h-8 w-28" />
            </div>
            <Button onClick={() => saveRate(parseInt(newYear), newRate)} disabled={!newRate}>Add / Update</Button>
          </div>
        </CardContent>
      </Card>

      {/* Calculator */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Calculate Reimbursement</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="w-20">Period</Label>
            <Select value={periodType} onChange={e => { setPeriodType(e.target.value); setResult(null); }} className="w-40">
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="ytd">Year to date</option>
              <option value="custom">Custom range</option>
            </Select>

            {periodType === 'month' && (
              <>
                <Select value={year} onChange={e => setYear(parseInt(e.target.value))} className="w-28">
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </Select>
                <Select value={month} onChange={e => setMonth(parseInt(e.target.value))} className="w-36">
                  {MONTHS.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
                </Select>
              </>
            )}
            {periodType === 'quarter' && (
              <>
                <Select value={year} onChange={e => setYear(parseInt(e.target.value))} className="w-28">
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </Select>
                <Select value={quarter} onChange={e => setQuarter(parseInt(e.target.value))} className="w-28">
                  <option value={1}>Q1</option><option value={2}>Q2</option><option value={3}>Q3</option><option value={4}>Q4</option>
                </Select>
              </>
            )}
            {periodType === 'custom' && (
              <>
                <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="w-40 h-9" />
                <span className="text-muted-foreground">to</span>
                <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="w-40 h-9" />
              </>
            )}

            <Button onClick={handleCalculate}>Calculate</Button>
          </div>

          {result && (
            <div className="pt-3 border-t space-y-3">
              <div className="text-sm text-muted-foreground">{result.start} to {result.end}</div>

              {result.breakdown.length > 0 && (
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[420px]">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="pb-1 w-24">Year</th>
                      <th className="pb-1">Miles</th>
                      <th className="pb-1">Rate</th>
                      <th className="pb-1">Reimbursement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.breakdown.map(b => (
                      <tr key={b.year} className="border-b border-border/50">
                        <td className="py-1 font-mono">{b.year}</td>
                        <td className="py-1">{b.miles.toFixed(1)}</td>
                        <td className="py-1">{b.rate == null ? <span className="text-destructive">no rate</span> : `$${b.rate}`}</td>
                        <td className="py-1">{b.amount == null ? <span className="text-destructive">—</span> : `$${b.amount.toFixed(2)}`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}

              <div className="space-y-1 text-sm font-semibold">
                <div>Total Miles: {result.total_miles.toFixed(1)}</div>
                <div className="text-lg">Total Reimbursement: ${result.total_amount.toFixed(2)}</div>
              </div>

              {result.missing_years.length > 0 && (
                <div className="text-sm text-destructive">
                  No rate set for {result.missing_years.join(', ')} — those miles are excluded from the total. Add the rate above to include them.
                </div>
              )}
              {result.breakdown.length === 0 && (
                <div className="text-sm text-muted-foreground">No trips in this period.</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reimbursement;
